import React, { useEffect, useState } from 'react';
import { API_BASE } from 'apiConfig';
import { useAppSelector } from 'hooks';
import './pricing.css';

interface ApiPlan {
  key: string;
  name: string;
  description: string;
  monthly_price_cents: number;
  yearly_price_cents: number;
  trial_days?: number;
  features: string[];
}

// Per-tier presentation (only the look — data comes from /billing/plans).
const PRESENTATION: Record<string, { dark: boolean; badge: string | null; highlight: boolean }> = {
  starter: { dark: false, badge: null, highlight: false },
  professional: { dark: false, badge: 'AI Agent', highlight: false },
  business: { dark: true, badge: 'AI Agents', highlight: true },
  platinum: { dark: true, badge: 'AI Agents', highlight: false }
};

const money = (cents: number) =>
  '$' + (cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });

const Check = () => (
  <span className="ecd-check" aria-hidden="true">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d="M20 6 9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const PricingPlans: React.FC = () => {
  const [plans, setPlans] = useState<ApiPlan[]>([]);
  const [isYearly, setIsYearly] = useState(false);
  const [message, setMessage] = useState('');
  const token = useAppSelector(state => state.auth.token);

  useEffect(() => {
    fetch(`${API_BASE}/billing/plans`)
      .then(r => r.json())
      .then(d => setPlans((d.plans || []).filter((p: ApiPlan) => p.key !== 'free')))
      .catch(() => setMessage('Could not load plans.'));
  }, []);

  const handleSubscribe = async (planKey: string) => {
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/billing/checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan_key: planKey, interval: isYearly ? 'year' : 'month' })
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
        return;
      }
      setMessage(data.error || 'Checkout is not available yet.');
    } catch {
      setMessage('Checkout is not available yet.');
    }
  };

  return (
    <div className="ecd-pricing">
      <div className="ecd-pricing__toggle">
        <span className={!isYearly ? 'active' : ''}>Monthly</span>
        <label className="ecd-switch">
          <input type="checkbox" checked={isYearly} onChange={() => setIsYearly(!isYearly)} />
          <span className="ecd-slider" />
        </label>
        <span className={isYearly ? 'active' : ''}>Yearly</span>
      </div>

      {message && <div className="ecd-pricing__msg">{message}</div>}

      <div className="ecd-pricing__grid">
        {plans.map(plan => {
          const pres = PRESENTATION[plan.key] || { dark: false, badge: null, highlight: false };
          const trialDays = plan.trial_days || 0;
          const cta = trialDays > 0 ? 'Start free trial' : 'Subscribe';
          // Always show a per-month figure: the month-to-month rate in Monthly
          // mode, or the cheaper annual rate (yearly total / 12) in Yearly mode.
          const perMonth = isYearly
            ? Math.round(plan.yearly_price_cents / 12)
            : plan.monthly_price_cents;
          const description = plan.description
            ? plan.description
            : `Per agent billed ${isYearly ? 'annually' : 'monthly'}`;
          const sub2 = isYearly
            ? `${money(plan.yearly_price_cents)} billed yearly`
            : `${money(plan.monthly_price_cents)} billed month-to-month`;

          return (
            <div
              key={plan.key}
              className={[
                'ecd-card',
                pres.dark ? 'ecd-card--dark' : '',
                pres.highlight ? 'ecd-card--highlight' : ''
              ]
                .join(' ')
                .trim()}
            >
              {pres.highlight && <div className="ecd-card__ribbon">Best value</div>}
              <div className="ecd-card__name">{plan.name}</div>

              <div className="ecd-card__price-wrap">
                {pres.badge && <span className="ecd-card__badge">{pres.badge}</span>}
                <span className="ecd-card__price">{money(perMonth)}</span>
              </div>
              <div className="ecd-card__period">MONTHLY</div>

              <div className="ecd-card__sub">{description}</div>
              <div className="ecd-card__sub2">{sub2}</div>

              <div className="ecd-card__divider" />

              <ul className="ecd-card__features">
                {plan.features.map((f, i) => (
                  <li key={i}>
                    <Check />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {trialDays > 0 && (
                <div className="ecd-card__trial">{trialDays}-day free trial</div>
              )}
              <button className="ecd-card__cta" onClick={() => handleSubscribe(plan.key)}>
                {cta}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PricingPlans;
